import i18n from "i18n-js";
import { FC, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { RootStateOrAny, useDispatch, useSelector } from "react-redux";
import { EuiBadge, EuiButton, EuiPageHeader, EuiSpacer } from "@elastic/eui";

import {
  CustomizeFilter,
  CustomizeTablePagination,
  DeleteAction,
  EditAction,
  FilterInput,
  Access,
} from "../../../components";
import { Paths, PERMISSIONS } from "../../../constants";
import { getDepartmentsApi } from "../../../network";
import {
  archiveManyEmployee,
  deleteManyEmployee,
  deleteOneEmployee,
  getListEmployee,
  unarchiveManyEmployee,
} from "../../../redux";
import { getInitParams, formatToLocalDate } from "../../../utils";

const List: FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [departments, setDepartments] = useState([]);
  const [params, setParams] = useState(getInitParams());

  const { data = [], total } = useSelector(
    (state: RootStateOrAny) => state?.employee?.list || {}
  );

  const { isLoadingRequest } = useSelector(
    (state: RootStateOrAny) => state.employee
  );

  const getDepartments = async () => {
    const departmentIds = data.map((item: any) => item.departmentId);
    if (!departmentIds || departmentIds.length === 0) {
      return setDepartments([]);
    }

    const result = await getDepartmentsApi({ids: departmentIds.join(',')});
    setDepartments(result.data || [])
  }

  useEffect(() => {
    dispatch(getListEmployee(params));
  }, [params]);

  useEffect(() => {
    getDepartments();
  }, [data]);

  const onChangePage = (value: any) => {
    setParams({ ...params, offset: value });
  };

  const onDeleteMany = (ids: string[]) => {
    dispatch(deleteManyEmployee({ ids }));
  };

  const onArchiveMany = (ids: string[]) => {
    dispatch(archiveManyEmployee({ ids, isArchived: true }));
  };

  const onUnarchiveMany = (ids: string[]) => {
    dispatch(unarchiveManyEmployee({ ids, isArchived: false }));
  };

  const columns = [
    {
      field: "name",
      name: "Tên",
      sortable: true,
    },
    {
      field: "mobilePhone",
      name: 'SDT',
    },
    {
      field: "workEmail",
      name: 'Email',
    },
    {
      field: "departmentId",
      name: 'Phòng ban',
      render: (departmentId: string) => {
        let name = '';
        const department: any = departments.find((item: any) => item._id === departmentId);
        if (department) name = department.name;
        return (<EuiBadge color={'primary'}>{name}</EuiBadge>)
      }
    },
    {
      field: "isArchived",
      name: "Lưu trữ",
      sortable: false,
      render: (isArchived: boolean) => (
        <EuiBadge color={isArchived ? "warning" : "ghost"}>
          {isArchived ? "Đã lưu trữ" : "Không"}
        </EuiBadge>
      ),
    },
    {
      field: "createdAt",
      name: "Khởi tạo",
      render: (time: string) => <p>{formatToLocalDate(time)}</p>,
    },
    {
      name: "Hành động",
      actions: [
        {
          render: (item: any) => {
            return (
              <Access permissionKey={PERMISSIONS.EMPLOYEE_UPDATE}>
                <EditAction
                  link={Paths.EMPLOYEE.EDIT.replace(":id", item._id)}
                />
              </Access>
            );
          },
        },
        {
          render: (item: any) => {
            return (
              <Access permissionKey={PERMISSIONS.EMPLOYEE_DELETE}>
                <DeleteAction
                  onOk={() => {
                    dispatch(deleteOneEmployee({ id: item._id }));
                  }}
                />
              </Access>
            );
          },
        },
      ],
    },
  ];

  const onChangeItemsPerPage = (per: any) => {
    setParams({ ...params, limit: per });
  };

  const filterInputs: FilterInput[] = [
    { name: "name", label: "Tên", type: "input", visible: true },
    { name: "mobilePhone", label: "Tên", type: "input", visible: true },
    { name: "workEmail", label: "Tên", type: "input" },
    {
      name: "isArchived",
      label: "Lưu trữ",
      type: "select",
      options: [
        { value: "", text: "Tất cả" },
        { value: "true", text: "Đã lưu trữ" },
        { value: "false", text: "Không" },
      ],
    },
  ];

  const onFilter = (data: any) => {
    const newFilter: any = { ...params, ...data };
    for (let key in newFilter) {
      if (newFilter[key] === "" || newFilter[key] === undefined) {
        delete newFilter[key];
      }
    }
    setParams({ ...newFilter, offset: 0 });
  };

  return (
    <>
      <EuiPageHeader
        bottomBorder
        className="mtb-page-header"
        pageTitle="Quản lý nhân viên"
        rightSideItems={[
          <Access permissionKey={PERMISSIONS.EMPLOYEE_CREATE}>
            <EuiButton
              iconSide="left"
              iconType="plusInCircle"
              fill
              onClick={() => navigate({ pathname: Paths.EMPLOYEE.CREATE })}
            >
              {i18n.t("common.createButton")}
            </EuiButton>
          </Access>,
        ]}
      />
      <EuiSpacer size="s" />

      <CustomizeFilter
        list={filterInputs}
        onFilter={onFilter}
        page={Paths.EMPLOYEE.LIST}
      />

      <EuiSpacer size="s" />

      <CustomizeTablePagination
        columns={columns}
        body={data}
        isLoading={isLoadingRequest}
        pagination={params}
        total={total}
        isSelectable={true}
        onChangePage={onChangePage}
        onChangeItemsPerPage={onChangeItemsPerPage}
        onDeleteMany={onDeleteMany}
        onArchiveMany={onArchiveMany}
        onUnarchiveMany={onUnarchiveMany}
      />
    </>
  );
};

export default List;
